import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileText,
  Image,
  MessageSquare,
  Zap,
  Code,
  Mail,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

const useCasesList = [
  {
    slug: "analyse-pdf",
    icon: FileText,
    color: "text-blue-400",
    bg: "bg-blue-400/10",
  },
  {
    slug: "generation-images",
    icon: Image,
    color: "text-purple-400",
    bg: "bg-purple-400/10",
  },
  {
    slug: "assistant-chat",
    icon: MessageSquare,
    color: "text-green-400",
    bg: "bg-green-400/10",
  },
  {
    slug: "redaction-ia",
    icon: Zap,
    color: "text-yellow-400",
    bg: "bg-yellow-400/10",
  },
  {
    slug: "generation-code",
    icon: Code,
    color: "text-red-400",
    bg: "bg-red-400/10",
  },
  {
    slug: "redaction-emails",
    icon: Mail,
    color: "text-pink-400",
    bg: "bg-pink-400/10",
  },
];

const UseCasesPage = () => {
  const { t } = useTranslation();
  const cases = t("useCases.list", { returnObjects: true }) || [];

  return (
    <PageLayout>
      <SEO
        title={t("useCases.index.title")}
        description={t("useCases.index.meta")}
        canonical="/use-cases"
      />
      <section className="section-y-sm px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="animate-in-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-primary text-sm font-medium mb-6">
              {t("useCases.index.badge")}
            </span>
            <h1
              className="animate-in-up font-heading text-fluid-h1 font-bold text-white mb-4"
              style={{ "--d": "80ms" }}
            >
              {t("useCases.index.title")}
            </h1>
            <p
              className="animate-in-up text-fluid-lead text-slate-400 max-w-2xl mx-auto"
              style={{ "--d": "160ms" }}
            >
              {t("useCases.index.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCasesList.map((uc, i) => {
              const data = cases[i] || {};
              const Icon = uc.icon;
              return (
                <Link
                  key={uc.slug}
                  to={`/use-cases/${uc.slug}`}
                  className="glass lift rounded-2xl p-6 hover:border-primary/40 group block"
                >
                  <div
                    className={`w-12 h-12 rounded-xl ${uc.bg} border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <Icon className={`w-6 h-6 ${uc.color}`} />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                    {data.title}
                  </h2>
                  <p className="text-slate-400 text-sm mb-4">{data.summary}</p>
                  <span className="text-primary text-sm flex items-center gap-1 font-medium">
                    {t("useCases.readMore")} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-16">
            <Link to="/register">
              <Button
                size="lg"
                className="btn-glow bg-primary-strong hover:bg-primary-strong/90 text-lg px-8"
              >
                {t("landing.cta")} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
export default UseCasesPage;
